/**
 * <yugal-order>: the buyer's post-purchase page. Reads ?o=<token> (from the order email), shows
 * each portrait with its versions, and lets the buyer approve one for print or ask for a
 * revision. After approval it shows the order's progress. Talks to the Yugal API (data-api).
 */
(() => {
  const POLL_MS = 15000;

  /**
   * Hangs an image as a canvas on a wall (same tree and classes as snippets/yugal-canvas.liquid,
   * styled in yugal.css). Spans, so it is valid inside buttons.
   */
  const canvasMockup = (img, size = '') => {
    const root = document.createElement('span');
    root.className = `y-canvas y-canvas--portrait${size ? ` y-canvas--${size}` : ''}`;
    const wall = document.createElement('span');
    wall.className = 'y-canvas__wall';
    const art = document.createElement('span');
    art.className = 'y-canvas__art';
    art.append(img);
    wall.append(art);
    root.append(wall);
    return root;
  };

  class YugalOrder extends HTMLElement {
    connectedCallback() {
      this.api = (this.dataset.api || '').replace(/\/+$/, '');
      this.strings = JSON.parse(this.querySelector('[data-strings]')?.textContent || '{}');
      this.token = new URLSearchParams(window.location.search).get('o') || '';
      this.chosen = {}; // itemId -> variant
      this.abort = new AbortController();
      this.load();
    }

    disconnectedCallback() {
      this.abort.abort();
      clearTimeout(this.timer);
    }

    $(selector) {
      return this.querySelector(selector);
    }

    t(key, vars = {}) {
      const text = this.strings[key] ?? key;
      return text.replace(/\{(\w+)\}/g, (_, name) => (name in vars ? vars[name] : `{${name}}`));
    }

    status(message = '', kind = 'info') {
      const el = this.$('[data-status]');
      el.textContent = message;
      el.dataset.kind = kind;
    }

    async request(path, { method = 'GET', body } = {}) {
      const response = await fetch(`${this.api}/orders/${encodeURIComponent(this.token)}${path}`, {
        method,
        headers: body ? { 'Content-Type': 'application/json' } : {},
        body: body ? JSON.stringify(body) : undefined,
        signal: this.abort.signal,
      });
      let data = {};
      try {
        data = await response.json();
      } catch {}
      if (!response.ok) {
        const error = new Error(data.message || data.error || 'error');
        error.status = response.status;
        throw error;
      }
      return data;
    }

    async load() {
      clearTimeout(this.timer);
      if (!this.token || !this.api) return this.notFound();
      try {
        this.data = await this.request('');
        this.render();
      } catch (err) {
        if (err.name === 'AbortError') return;
        if (err.status === 404) return this.notFound();
        this.status(this.t('error'), 'error');
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    /** No link, or a link that doesn't work: offer to email the link to the order's address. */
    notFound() {
      this.$('[data-title]').textContent = this.t('find_title');
      this.$('[data-intro]').textContent = this.token ? this.t('not_found') : this.t('find_intro');
      this.removeAttribute('aria-busy');
      const form = this.$('[data-find]');
      if (!form || !this.api) return;
      form.hidden = false;
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = form.querySelector('button');
        const data = new FormData(form);
        button.disabled = true;
        this.status();
        try {
          const response = await fetch(`${this.api}/orders/lookup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: data.get('email'), order: data.get('order') }),
          });
          if (!response.ok) throw new Error(String(response.status));
          form.hidden = true;
          this.status(this.t('find_sent'));
        } catch {
          button.disabled = false;
          this.status(this.t('error'), 'error');
        }
      });
    }

    render() {
      const { order, items } = this.data;
      const open = items.some((i) => i.can_approve || i.status === 'revision_in_progress');
      const done = order.status === 'delivered' ? 'title_delivered' : order.status === 'shipped' ? 'title_shipped' : 'title_done';
      this.$('[data-eyebrow]').textContent = this.t('eyebrow', { name: order.name });
      this.$('[data-title]').textContent = open ? this.t('title') : this.t(done);
      this.$('[data-intro]').textContent = order.status === 'cancelled' ? this.t('cancelled') : open ? this.t('intro') : '';
      this.$('[data-intro]').hidden = !this.$('[data-intro]').textContent;

      const host = this.$('[data-items]');
      host.replaceChildren(...items.map((item) => this.renderItem(item, order)));
      if (items.some((i) => i.status === 'revision_in_progress')) this.timer = setTimeout(() => this.load(), POLL_MS);
    }

    renderItem(item, order) {
      const el = document.createElement('article');
      el.className = 'yo__item';
      el.dataset.item = item.id;
      const variants = item.variants || [];
      const chosen = this.chosen[item.id] || item.variant || variants[0]?.variant;
      this.chosen[item.id] = chosen;

      // Portrait and its versions.
      const figure = document.createElement('figure');
      figure.className = 'yo__figure';
      if (item.print_url) {
        // Past our quality check: show the final print itself, not the watermarked preview.
        const img = document.createElement('img');
        img.alt = item.style?.name || '';
        img.width = 800;
        img.height = 1200;
        img.src = item.print_url;
        img.addEventListener('error', () => this.refreshOnce());
        const caption = document.createElement('figcaption');
        caption.className = 'y-meta';
        caption.textContent = this.t('print_caption');
        figure.append(canvasMockup(img), caption);
        if (item.ar_url) figure.append(this.arButton(item.ar_url, item.style?.name));
      } else if (variants.length) {
        const img = document.createElement('img');
        img.alt = item.style?.name || '';
        img.width = 800;
        img.height = 1200;
        img.src = (variants.find((v) => v.variant === chosen) || variants[0]).preview_url;
        img.addEventListener('error', () => this.refreshOnce());
        figure.append(canvasMockup(img));
        const ar = this.arButton((variants.find((v) => v.variant === chosen) || variants[0]).ar_url, item.style?.name);
        if (variants.length > 1) figure.append(this.renderVersions(item, variants, chosen, img, ar));
        figure.append(ar);
      }

      // The side: style, then what the buyer can do or how far along it is.
      const side = document.createElement('div');
      side.className = 'yo__side';
      if (item.style) {
        const label = document.createElement('p');
        label.className = 'y-eyebrow';
        label.textContent = this.t('style_label');
        const name = document.createElement('p');
        name.className = 'yo__style';
        name.textContent = item.style.name;
        side.append(label, name);
      }

      if (item.status === 'revision_in_progress') {
        const working = document.createElement('p');
        working.className = 'y-body yo__working';
        working.append(document.createElement('i'), this.t('revision_running'));
        side.append(working);
      } else if (item.can_approve) {
        if (item.returned) side.append(this.para(this.t('returned_note'), 'y-body yo__notice'));
        side.append(...this.renderActions(item));
      } else if (order.status === 'cancelled' || item.status === 'cancelled') {
        side.append(this.para(this.t('cancelled')));
      } else if (item.status === 'needs_attention') {
        side.append(this.para(this.t('stage_attention')));
      } else {
        side.append(this.renderStages(item, order));
      }

      el.append(figure, side);
      return el;
    }

    /** "See it on your wall" (assets/yugal-ar.js opens it); hidden when there is no model link. */
    arButton(src, alt = '') {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'y-btn y-btn--ghost y-ar-trigger yo__ar';
      button.textContent = this.t('ar_button');
      button.dataset.arAlt = alt;
      button.hidden = !src;
      if (src) button.dataset.arSrc = src;
      return button;
    }

    renderVersions(item, variants, chosen, img, ar) {
      const group = document.createElement('div');
      group.className = 'yo__versions';
      group.setAttribute('role', 'radiogroup');
      group.setAttribute('aria-label', this.t('versions_label'));
      const select = (variant, focus) => {
        this.chosen[item.id] = variant;
        const picked = variants.find((v) => v.variant === variant);
        img.src = picked.preview_url;
        if (ar) {
          ar.hidden = !picked.ar_url;
          if (picked.ar_url) ar.dataset.arSrc = picked.ar_url;
        }
        group.querySelectorAll('button').forEach((b) => {
          const on = b.dataset.version === variant;
          b.setAttribute('aria-checked', String(on));
          b.tabIndex = on ? 0 : -1;
          if (on && focus) b.focus();
        });
      };
      variants.forEach((v, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'yo__version';
        button.dataset.version = v.variant;
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-label', this.t('version_n', { n: index + 1 }));
        button.disabled = !item.can_approve;
        const thumb = document.createElement('img');
        thumb.src = v.preview_url;
        thumb.alt = '';
        thumb.width = 160;
        thumb.height = 240;
        thumb.loading = 'lazy';
        button.append(canvasMockup(thumb, 'sm'));
        button.addEventListener('click', () => select(v.variant, false));
        button.addEventListener('keydown', (event) => {
          const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
          if (!step) return;
          event.preventDefault();
          select(variants[(index + step + variants.length) % variants.length].variant, true);
        });
        group.append(button);
      });
      select(chosen, false);
      return group;
    }

    renderActions(item) {
      const nodes = [];
      // Silence never means print: the portrait waits for their approval.
      nodes.push(this.para(this.t('deadline'), 'y-meta'));

      // May we show it? Unticked: saying nothing means no.
      const consent = document.createElement('label');
      consent.className = 'yo__consent y-meta';
      const box = document.createElement('input');
      box.type = 'checkbox';
      const words = document.createElement('span');
      words.textContent = this.t('exhibit_consent');
      consent.append(box, words);
      this.exhibit = box;

      const approve = document.createElement('button');
      approve.type = 'button';
      approve.className = 'y-btn';
      approve.textContent = this.t('approve');
      approve.addEventListener('click', () => this.approve(item, approve));
      nodes.push(consent, approve);

      const left = item.revisions_left;
      const count = this.para(left === 0 ? this.t('revisions_none') : left === 1 ? this.t('revisions_left_one') : this.t('revisions_left', { count: left }), 'y-meta');
      if (!item.can_revise) {
        nodes.push(count);
        return nodes;
      }

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'y-link';
      toggle.textContent = this.t('revise_toggle');
      toggle.setAttribute('aria-expanded', 'false');

      const panel = document.createElement('div');
      panel.className = 'yo__revise';
      panel.hidden = true;
      panel.id = `yo-revise-${item.id}`;
      toggle.setAttribute('aria-controls', panel.id);
      const label = document.createElement('label');
      label.className = 'y-eyebrow';
      label.textContent = this.t('revise_style');
      label.htmlFor = `yo-style-${item.id}`;
      const select = document.createElement('select');
      select.id = `yo-style-${item.id}`;
      for (const style of this.data.styles) {
        const option = document.createElement('option');
        option.value = style.slug;
        option.textContent = style.slug === item.style?.slug ? `${style.name}, ${this.t('revise_same')}` : style.name;
        option.selected = style.slug === item.style?.slug;
        select.append(option);
      }
      const submit = document.createElement('button');
      submit.type = 'button';
      submit.className = 'y-btn y-btn--ghost';
      submit.textContent = this.t('revise_submit');
      submit.addEventListener('click', () => this.revise(item, select.value, submit));
      panel.append(this.para(this.t('revision_note'), 'y-meta'), label, select, submit, count);

      toggle.addEventListener('click', () => {
        panel.hidden = !panel.hidden;
        toggle.setAttribute('aria-expanded', String(!panel.hidden));
        if (!panel.hidden) select.focus();
      });
      nodes.push(toggle, panel);
      return nodes;
    }

    renderStages(item, order) {
      const keys = ['stage_approved', 'stage_print', 'stage_qc', 'stage_printing', 'stage_shipped', 'stage_delivered'];
      let current = 1;
      if (item.status === 'awaiting_qc') current = 2;
      if (item.status === 'qc_approved') current = 3;
      if (order.status === 'shipped') current = 4;
      if (order.status === 'delivered') current = 6;
      const list = document.createElement('ol');
      list.className = 'yo__stages';
      keys.forEach((key, index) => {
        const li = document.createElement('li');
        li.className = 'yo__stage y-body';
        li.textContent = this.t(key);
        if (index < current) li.toggleAttribute('data-done', true);
        if (index === current) li.toggleAttribute('data-current', true);
        list.append(li);
      });
      const wrap = document.createElement('div');
      wrap.append(this.para(this.t('approved_note'), 'y-body'), list);
      if (order.reveal_url) {
        const reveal = document.createElement('a');
        reveal.className = 'y-btn';
        reveal.href = order.reveal_url;
        reveal.textContent = this.t(order.is_gift ? 'reveal_gift' : 'reveal_self');
        wrap.append(this.para(this.t(order.is_gift ? 'reveal_gift_note' : 'reveal_self_note'), 'y-body'), reveal);
      }
      const tracking = order.tracking;
      if (tracking && (tracking.url || tracking.number)) {
        const line = this.para([tracking.carrier, tracking.number].filter(Boolean).join(' · '), 'y-meta');
        if (tracking.url) {
          const link = document.createElement('a');
          link.className = 'y-btn y-btn--ghost';
          link.href = tracking.url;
          link.target = '_blank';
          link.rel = 'noopener';
          link.textContent = this.t('track');
          wrap.append(line, link);
        } else wrap.append(line);
      }
      wrap.style.display = 'grid';
      wrap.style.gap = '1.5rem';
      return wrap;
    }

    para(text, className = 'y-body') {
      const p = document.createElement('p');
      p.className = className;
      p.textContent = text;
      return p;
    }

    async approve(item, button) {
      if (!window.confirm(this.t('confirm_approve'))) return;
      const label = button.textContent;
      button.disabled = true;
      button.textContent = this.t('approving');
      this.status();
      try {
        await this.request(`/items/${item.id}/approve`, { method: 'POST', body: { variant: this.chosen[item.id], exhibit: Boolean(this.exhibit?.checked) } });
        await this.load();
        this.$('[data-title]').focus();
      } catch (err) {
        button.disabled = false;
        button.textContent = label;
        this.status(err.message && err.message !== 'error' ? err.message : this.t('error'), 'error');
      }
    }

    async revise(item, styleSlug, button) {
      const label = button.textContent;
      button.disabled = true;
      button.textContent = this.t('revising');
      this.status();
      try {
        await this.request(`/items/${item.id}/revise`, { method: 'POST', body: { style_slug: styleSlug } });
        await this.load();
      } catch (err) {
        button.disabled = false;
        button.textContent = label;
        this.status(err.message && err.message !== 'error' ? err.message : this.t('error'), 'error');
      }
    }

    /** Preview links expire after ten minutes; reload once if an image fails. */
    refreshOnce() {
      if (this.refreshed) return;
      this.refreshed = true;
      this.load();
    }
  }

  if (!customElements.get('yugal-order')) customElements.define('yugal-order', YugalOrder);
})();
